import {
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
} from '@mui/material';
import { closeSnackbar, useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentReturnedOutlinedIcon from '@mui/icons-material/AssignmentReturnedOutlined';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import { GridColDef } from '@mui/x-data-grid';
import { showLabel } from '@/utils/helper';
import { AwardType, OnoffType } from '@/api/enum/common.enum';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import { DefaultValues, useForm } from 'react-hook-form';
import { DataGrid } from '../AchievementSummaryPage';
import { useParams } from 'react-router-dom';
import {
	useGetAchievementSingleSortListQuery,
	useSetAchievementSingleStatusMutation,
} from '@/api/task/achievementApiSlice';
import { TaskAchievementRewardDetailSingleRequest } from '@/api/DTO/task/achievement.interface';
import PublishedChip from '@/common/components/PublishedChip';
import { PUBLISHED_LIST_, PUBLISHED_LISTB } from '@/common/constants/common';

interface Props {}

export default function SetSingleStatusDialog(props: Props) {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [open, setOpen] = useState(false);
	const [setAchievementSingleStatus] = useSetAchievementSingleStatusMutation();
	const { achievementId } = useParams();
	const awardTypeList = [
		{ id: AwardType.Int, name: t('taskManage.Int') },
		{ id: AwardType.Prop, name: t('taskManage.Prop') },
		{ id: AwardType.IntProp, name: t('taskManage.IntProp') },
	];

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetAchievementSingleSortListQuery(achievementId || '', {
			refetchOnMountOrArgChange: true,
			skip: !open,
		});

	const defaultValues: DefaultValues<any> = {};

	const methods = useForm<any>({
		defaultValues,
	});

	const onSubmit = async () => {
		const data: { id: string; published: boolean }[] = Object.entries(
			methods.getValues()
		).map(([key, value]) => ({
			id: key,
			published: value == 1 ? true : false,
		}));
		const response = await setAchievementSingleStatus({
			publishList: data,
		});
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.changeStatusSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
		}
	};

	const handleClose = () => {
		setOpen(false);
	};

	const columns: GridColDef[] = [
		{
			field: 'sort',
			headerName: t('achievementList.level'),
			flex: 25,
		},
		{
			field: 'finishedNum',
			headerName: t('achievementList.finishTotal'),
			flex: 25,
		},
		{
			field: 'rewardType',
			headerName: t('achievementList.rewardType'),
			flex: 25,
			renderCell: (params: any) =>
				showLabel(params.row.rewardType, awardTypeList),
		},
		{
			field: 'rewardPoints',
			headerName: t('achievementList.rewardPoints'),
			flex: 25,
		},
		{
			field: 'rewardDesc',
			headerName: t('achievementList.rewardProp'),
			flex: 25,
		},
		{
			field: 'published',
			headerName: t('achievementList.taskStatus'),
			flex: 25,
			renderCell: (params: any) => (
				<PublishedChip
					published={params.row.published}
					publishList={PUBLISHED_LISTB}
				/>
			),
		},
		{
			field: 'changePublished',
			headerName: t('meditation.onoff'),
			flex: 40,
			renderCell: (params: any) => (
				<AppRadioGroup
					control={methods.control}
					name={params.row.id}
					row
					options={PUBLISHED_LIST_}
				/>
			),
		},
	];

	const transformResponse = useCallback(
		(response?: TaskAchievementRewardDetailSingleRequest[]) => {
			if (!response) return;
			const newResponse: DataGrid[] = [];
			response.forEach((item) => {
				newResponse.push({
					...item,
				});

				methods.setValue(
					item.id,
					item.published ? OnoffType.on : OnoffType.off
				);
			});

			return newResponse;
		},
		[]
	);

	const formContent = (
		<>
			<Button
				variant='contained'
				style={{ alignItems: 'center' }}
				endIcon={<AssignmentReturnedOutlinedIcon />}
				onClick={() => setOpen(true)}
			>
				{t('meditation.onoff')}
			</Button>
			<Dialog
				open={open}
				onClose={() => handleClose()}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='xl'
				fullWidth
			>
				<DialogTitle variant='h5'>{t('meditation.onoff')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={() => handleClose()}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
				<DialogContent>
					<Grid container alignItems='stretch' columnSpacing={3} marginTop={3}>
						<Grid item xs display='flex'>
							<LocalDataGrid
								rows={transformResponse(propertySummary?.data) ?? []}
								columns={columns}
								rowCount={0}
								getRowId={(row) => row.id}
								pagination={false}
							/>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => handleClose()}>{t('button.cancel')}</Button>

					<Button type='submit' variant='contained' onClick={() => onSubmit()}>
						{t('button.save')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
	return <>{formContent}</>;
}
